import { Fragment, useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import LineGauge from 'app/components/Gauges/LineGauge';
import TeamAvatar from 'app/components/TeamAvatar';
import { getStatWinner } from 'app/utils/entities/match_data';
import StatValue from 'app/components/StatValue';
import ArrowIcon from 'app/icons/arrow-min.svg';
import { localizedMapName } from 'app/i18n/utils';
import { withBackgroundImage } from 'app/utils/style';
import { getCDNUrl } from 'app/utils/api_url';

import { getCsMapOpponentStats } from './utils';
import style from './style.module.scss';

interface Props {
  data: TeamsStatsBlockMatch;
}

const MapPool: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const mapsData = useMemo(() => data.maps_winrate || [], [data.maps_winrate]);

  const opponent1 = data.opponents.at(0);
  const opponent2 = data.opponents.at(1);

  const [expandedId, setExpandedId] = useState<number | null>(mapsData.at(0)?.id || null);

  useEffect(() => {
    setExpandedId(mapsData.at(0)?.id || null);
  }, [mapsData]);

  const toggleExpanded = (mapId: number) => {
    if (mapId === expandedId) {
      setExpandedId(null);
    } else {
      setExpandedId(mapId);
    }
  };

  return (
    <BlockGradTemplate
      hasData={Boolean(mapsData.length)}
      title={t('block-pool-map-title')}
      opponentsLogo={[opponent1, opponent2]}
      contentClassName={style.maps}
    >
      {mapsData.map(map => {
        const isExpanded = map.id === expandedId;

        const mapOpponent1 = opponent1?.id ? map.opponents[opponent1.id] : undefined;
        const mapOpponent2 = opponent2?.id ? map.opponents[opponent2.id] : undefined;

        const mapWinrateOpponent1 =
          mapOpponent1 && mapOpponent1.total > 0
            ? Math.round((mapOpponent1.wins / mapOpponent1.total) * 100)
            : undefined;
        const mapWinrateOpponent2 =
          mapOpponent2 && mapOpponent2.total > 0
            ? Math.round((mapOpponent2.wins / mapOpponent2.total) * 100)
            : undefined;

        const expandContainerClassname = classNames(style.expandContainer, {
          [style.expanded]: isExpanded
        });

        const mapOpponentsStats = getCsMapOpponentStats(
          mapOpponent1 ? { ...mapOpponent1, winrate: mapWinrateOpponent1! } : undefined,
          mapOpponent2 ? { ...mapOpponent2, winrate: mapWinrateOpponent2! } : undefined
        );

        return (
          <div key={map.id}>
            <button
              type="button"
              onClick={() => toggleExpanded(map.id)}
              style={withBackgroundImage(getCDNUrl(map.image_url))}
              className={style.mapHeader}
            >
              <div className={style.opponentWinrateContainer}>
                <div className={style.opponentWinrate}>
                  <div className={style.opponentWinrateTitle}>
                    {t('block-pool-map-stat-winrate')}
                  </div>
                  {mapOpponent1 && (
                    <div className={style.opponentWinrateNumberOfMaps}>
                      {t('block-pool-map-number_of_maps', { count: mapOpponent1.total })}
                    </div>
                  )}
                </div>
                <LineGauge
                  value={mapWinrateOpponent1}
                  orientation="reversed"
                  variation="grad_team_1"
                  valuePosition="start"
                />
              </div>
              <div className={style.mapNameContainer}>
                <span className={style.mapName}>{localizedMapName(map, t)}</span>
                <ArrowIcon
                  className={classNames({
                    [style.arrowIconOpened]: isExpanded
                  })}
                />
              </div>
              <div className={style.opponentWinrateContainer}>
                <div className={style.opponentWinrate}>
                  <div className={style.opponentWinrateTitle}>
                    {t('block-pool-map-stat-winrate')}
                  </div>
                  {mapOpponent2 && (
                    <div className={style.opponentWinrateNumberOfMaps}>
                      {t('block-pool-map-number_of_maps', { count: mapOpponent2.total })}
                    </div>
                  )}
                </div>
                <LineGauge
                  value={mapWinrateOpponent2}
                  orientation="normal"
                  variation="grad_team_2"
                  valuePosition="start"
                />
              </div>
            </button>
            <div className={expandContainerClassname}>
              <div className={style.statsHeader}>{t('block-pool-map-stat-team')}</div>
              <div className={style.statsValue}>
                <TeamAvatar team={opponent1} />
              </div>
              <div className={style.statsValue}>
                <TeamAvatar team={opponent2} />
              </div>
              {mapOpponentsStats.map(stat => {
                const [isTeam1StatWinner, isTeam2StatWinner] = getStatWinner(stat);
                return (
                  <Fragment key={stat.name}>
                    <div className={style.statsHeader}>{t(`block-pool-map-stat-${stat.name}`)}</div>
                    <div
                      className={classNames(style.statsValue, {
                        [style.statHighlightedTeam1]: isTeam1StatWinner
                      })}
                    >
                      <StatValue value={stat.team1Value} displayFunction={stat.displayFunction} />
                    </div>
                    <div
                      className={classNames(style.statsValue, {
                        [style.statHighlightedTeam2]: isTeam2StatWinner
                      })}
                    >
                      <StatValue value={stat.team2Value} displayFunction={stat.displayFunction} />
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </BlockGradTemplate>
  );
};

MapPool.displayName = 'MapPool';

export default MapPool;
